import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchSlots,
  createBooking,
  ApiError,
  type BusinessInfo,
  type ServiceInfo,
  type Slot,
} from "../lib/api.ts";
import { BusinessHeader } from "../components/BusinessHeader.tsx";
import { DatePicker } from "../components/DatePicker.tsx";
import { SlotGrid } from "../components/SlotGrid.tsx";
import { CustomerForm, type CustomerFormData } from "../components/CustomerForm.tsx";
import { ConfirmSummary } from "../components/ConfirmSummary.tsx";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  slug: string;
  business: BusinessInfo;
  services: ServiceInfo[];
  staff: Array<{ id: string; name: string }>;
}

type Step = "schedule" | "details" | "confirm";

// ─── Component ───────────────────────────────────────────────────────────────

export function SimpleFlow({ slug, business, services }: Props) {
  const navigate = useNavigate();

  // Use the first active service; BookingShell guarantees at least one exists
  const firstService = services[0]!;

  const today = new Date().toISOString().split("T")[0] ?? "";
  const [step, setStep] = useState<Step>("schedule");
  const [date, setDate] = useState<string>(today);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [customerData, setCustomerData] = useState<CustomerFormData | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Fetch slots whenever date changes
  useEffect(() => {
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    fetchSlots(slug, date, [firstService.id])
      .then(setSlots)
      .catch(() => setSlotsError("Failed to load available slots."))
      .finally(() => setSlotsLoading(false));
  }, [date, slug, firstService.id]);

  function handleCustomerSubmit(data: CustomerFormData) {
    setCustomerData(data);
    setStep("confirm");
  }

  async function handleConfirm() {
    if (!selectedSlot || !customerData) return;
    setBookingLoading(true);
    setBookingError(null);
    try {
      const confirmation = await createBooking(slug, {
        service_ids: [firstService.id],
        staff_id: undefined,
        date: date,
        start_time: selectedSlot.start,
        meeting_mode: firstService.meeting_mode,
        customer: {
          name: customerData.name,
          email: customerData.email,
          phone: customerData.phone,
          notes: customerData.notes || undefined,
        },
      });
      if (confirmation.checkout_url) {
        window.location.href = confirmation.checkout_url;
        return;
      }

      const params = new URLSearchParams({
        token: confirmation.cancel_token,
        id: confirmation.appointment_id,
        date: date,
        start_time: selectedSlot.start,
        service: firstService.name,
        staff: "",
        meeting_mode: firstService.meeting_mode,
      });
      navigate(`/${slug}/success?${params.toString()}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBookingError("That time was just taken. Please pick another slot.");
        setStep("schedule");
        setSelectedSlot(null);
      } else {
        setBookingError("Booking failed. Please try again.");
      }
    } finally {
      setBookingLoading(false);
    }
  }

  const meetingModes = [...new Set(services.map((s) => s.meeting_mode))];

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "var(--font)" }}>
      <BusinessHeader business={business} meetingModes={meetingModes} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

        {/* ── Step 1: Date + Time ── */}
        {step === "schedule" && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Book an appointment</h2>
              {firstService && (
                <p className="text-sm text-gray-500 mt-1">
                  {firstService.name} &middot; {firstService.duration_min} min
                </p>
              )}
            </div>

            <DatePicker
              selected={date}
              onSelect={(d) => { setDate(d); setSelectedSlot(null); }}
            />

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Available times</p>
              <SlotGrid
                slots={slots}
                selected={selectedSlot?.start ?? null}
                onSelect={setSelectedSlot}
                loading={slotsLoading}
                error={slotsError}
              />
            </div>

            {selectedSlot && (
              <button
                type="button"
                onClick={() => setStep("details")}
                className="w-full py-3 px-4 rounded-[var(--radius)] text-white text-sm font-semibold transition-opacity hover:opacity-90"
                style={{ background: "var(--accent)", borderRadius: "var(--radius)" }}
              >
                Continue
              </button>
            )}

            {bookingError && (
              <p className="text-sm text-red-600 text-center">{bookingError}</p>
            )}
          </div>
        )}

        {/* ── Step 2: Details (notes prominent) ── */}
        {step === "details" && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Tell us more</h2>
            <CustomerForm
              onSubmit={handleCustomerSubmit}
              onBack={() => setStep("schedule")}
              notesProminent={true}
            />
          </div>
        )}

        {/* ── Step 3: Confirm ── */}
        {step === "confirm" && selectedSlot && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Review your booking</h2>
            <ConfirmSummary
              serviceNames={[firstService.name]}
              staffName={null}
              date={date}
              time={selectedSlot.start}
              durationMin={firstService.duration_min}
              priceCents={firstService.price_cents}
              currency={firstService.currency}
              meetingMode={firstService.meeting_mode}
              onConfirm={handleConfirm}
              onBack={() => setStep("details")}
              loading={bookingLoading}
              error={bookingError}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-gray-400">
        Powered by{" "}
        <a
          href="https://zedule.app"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium hover:text-gray-600 transition-colors"
        >
          Zedule
        </a>
      </div>
    </div>
  );
}
